import { minBy } from 'es-toolkit';

import HalfSpace3D from './HalfSpace3D';
import Point3D from './Point3D';

interface MutableRenderData {
  vertices: ReadonlyArray<Point3D>;
  // A triangulation of the faces. The numbers are indices into the vertices array.
  triangles: ReadonlyArray<Readonly<[number, number, number]>>;
  // Each subarray contains the indices of the triangles in the triangles array that make up a face.
  faces: ReadonlyArray<ReadonlyArray<number>>;
}
type RenderData = Readonly<MutableRenderData>;

// A bounded intersection of a finite number of half-spaces with nonempty interior.
class ConvexPolyhedron3D {
  // The half-spaces that, when intersected, give the polyhedron
  private readonly _halfSpaces: ReadonlyArray<HalfSpace3D>;
  private _renderData: RenderData | null = null;

  private constructor(halfSpaces: ReadonlyArray<HalfSpace3D>) {
    this._halfSpaces = [...halfSpaces];
  }

  static intersectionOfHalfSpaces(halfSpaces: ReadonlyArray<HalfSpace3D>) {
    const result = new ConvexPolyhedron3D(halfSpaces);
    result._renderData = result.computeRenderData();
    return result._renderData && result;
  }

  get renderData() {
    return this._renderData!;
  }

  // Find the edges of the face that is contained in the specified half-space's boundary, and return other half-spaces
  // whose boundaries contain those edges. Return null if the half-space's boundary is determined not to contain a face
  // of the polyhedron.
  private computeFace(halfSpace: HalfSpace3D): ReadonlyArray<HalfSpace3D> | null {
    const plane = halfSpace.boundary;
    const halfSpaceWithCrossingBoundary = this._halfSpaces.find(
      (halfSpace) => !!halfSpace.boundary.intersectWithPlane(plane),
    );
    if (!halfSpaceWithCrossingBoundary) {
      return null;
    }
    let previousHalfSpace = halfSpaceWithCrossingBoundary;
    let edgeLine = plane.intersectWithPlane(halfSpaceWithCrossingBoundary.boundary)!;
    // Ensure correct orientation of face for three.js rendering
    if (
      halfSpace.direction
        .crossProductDirection(halfSpaceWithCrossingBoundary.direction)!
        .dotProduct(edgeLine.direction) < 0
    ) {
      edgeLine = edgeLine.reverse();
    }
    const edgeHalfSpaces = [halfSpaceWithCrossingBoundary];
    let duplicateIndex = -1;
    // Loop around the face's boundary collecting edges until we find one of the edges a second time.
    while (duplicateIndex < 0) {
      // The half-space whose boundary contains the next edge.
      const nextHalfSpace = minBy(
        this._halfSpaces.filter(
          (halfSpace) =>
            // We only want half-spaces whose interiors are facing the direction whence we are coming.
            halfSpace.direction.dotProduct(edgeLine.direction) < 0 && halfSpace.boundary.intersectWithLine(edgeLine),
        ),
        // We want the first matching half-space that we hit as we proceed along the line.
        (halfSpace) => halfSpace.boundary.intersectWithLine(edgeLine)!.dotProduct(edgeLine.direction.toPoint()),
      );
      if (!nextHalfSpace) {
        return null;
      }
      duplicateIndex = edgeHalfSpaces.lastIndexOf(nextHalfSpace);
      edgeHalfSpaces.push(nextHalfSpace);
      edgeLine = plane.intersectWithPlane(nextHalfSpace.boundary)!;
      // Keep looping around in the same direction.
      if (edgeLine.direction.dotProduct(previousHalfSpace.direction) < 0) {
        edgeLine = edgeLine.reverse();
      }
      previousHalfSpace = nextHalfSpace;
    }
    for (let index = duplicateIndex + 1; index < edgeHalfSpaces.length; index += 1) {
      const vertex = halfSpace.boundary.intersectWithPlanes(
        edgeHalfSpaces[index - 1].boundary,
        edgeHalfSpaces[index].boundary,
      )!;
      // Check that the proposed face is not outside the polyhedron.
      if (!this._halfSpaces.every((halfSpace) => halfSpace.containsPointOnInsideOrBoundary(vertex))) {
        return null;
      }
    }
    return edgeHalfSpaces.slice(duplicateIndex + 1);
  }
  private computeRenderData(): RenderData | null {
    const faces = this._halfSpaces.map((halfSpace) => this.computeFace(halfSpace));
    let faceCount = 0;
    for (const face of faces) {
      if (!face) {
        continue;
      }
      faceCount += 1;
      // Check that the face is bounded by other valid faces.
      for (const edgeHalfSpace of face) {
        if (!faces[this._halfSpaces.indexOf(edgeHalfSpace)]) {
          return null;
        }
      }
    }
    if (faceCount < 4) {
      return null;
    }
    const result = {
      vertices: [] as Point3D[],
      triangles: [] as Readonly<[number, number, number]>[],
      faces: [] as ReadonlyArray<number>[],
    };
    for (let index = 0; index < faces.length; index += 1) {
      const face = faces[index];
      if (!face) {
        continue;
      }
      const plane = this._halfSpaces[index].boundary;
      const firstVertexIndex = result.vertices.length;
      const surface = [];
      for (let edgeIndex = 0; edgeIndex < face.length; edgeIndex += 1) {
        if (edgeIndex >= 2) {
          surface.push(result.triangles.length);
          result.triangles.push([firstVertexIndex, result.vertices.length - 1, result.vertices.length]);
        }
        const vertex = plane.intersectWithPlanes(
          face[edgeIndex].boundary,
          face[(edgeIndex + 1) % face.length].boundary,
        )!;
        result.vertices.push(vertex);
      }
      result.faces.push(surface);
    }
    return result;
  }
}

export default ConvexPolyhedron3D;
